import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CloseOnBack } from "../components/common/useCloseOnBack";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdConfirmationNumber, MdEventAvailable } from 'react-icons/md';
import { FiCalendar, FiChevronRight, FiCreditCard, FiGift, FiHeadphones, FiLock, FiSettings, FiStar, FiTag, FiX } from 'react-icons/fi';
import { supabase } from '../supabase';
import { fetchOwnProfile } from '../utils/userProfile';
import { toTicketShape } from '../utils/ticketMapping';
import { isRoundBased, isRoundPlaying } from '../features/tickets/roundTicket';
import { computeTicketRemainingSeconds } from '../features/tickets/ticketTime';
import { getDaysUntilExpiry, isExpiringSoon } from '../utils/couponExpiry';
import BottomSheet from '../components/common/BottomSheet';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import '../styles/global.css';
import '../styles/Home.css';
import '../features/auth/SettingsTabs/Coupons.css';
import '../features/auth/components/Profile.css';
import ParkStatusCard from '../components/home/ParkStatusCard';

function ProfileCardButton({ icon, title, description, to, onClick, color = 'blue', vibrate = 10 }) {
  const navigate = useNavigate();

  const handleActivate = () => {
    window.navigator.vibrate?.(vibrate);
    if (onClick) {
      onClick();
      return;
    }
    if (typeof to === 'string' && /^https?:\/\//i.test(to)) {
      window.open(to, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(to);
  };

  return (
    <div
      className="profile-menu-row"
      onClick={handleActivate}
      tabIndex={0}
      role="button"
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      <div className={`profile-menu-icon profile-menu-icon--${color}`}>{icon}</div>
      <div className="profile-menu-text">
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
      <FiChevronRight size={16} className="profile-menu-arrow" />
    </div>
  );
}

// Reuses the same user-doc cache Profile.jsx writes, so the welcome card can paint
// the logged-in name/points instantly on repeat visits instead of skeleton-flashing
// every single time while onAuthStateChanged + the Firestore doc fetch resolve.
const getCachedUserDoc = () => {
  try { return JSON.parse(localStorage.getItem('pws_user_cache')); } catch { return null; }
};

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cachedUserDoc = getCachedUserDoc();
  const hasAuthFlag = localStorage.getItem('pws_auth') === '1';
  const [likelyLoggedIn, setLikelyLoggedIn] = useState(Boolean(cachedUserDoc && hasAuthFlag));
  const [profileLoading, setProfileLoading] = useState(!(cachedUserDoc && hasAuthFlag));
  const [userName, setUserName] = useState(cachedUserDoc?.name || '');
  const [userPoints, setUserPoints] = useState(cachedUserDoc?.points || 0);
  const [userCoupons, setUserCoupons] = useState(() => {
    if (!hasAuthFlag) return [];
    try { return JSON.parse(localStorage.getItem('pws_home_coupons_cache')) || []; } catch { return []; }
  });
  const [couponsLoading, setCouponsLoading] = useState(() => {
    if (!hasAuthFlag) return true;
    try { return !localStorage.getItem('pws_home_coupons_cache'); } catch { return true; }
  });
  const [showLangSheet, setShowLangSheet] = useState(false);
  const [activeModalItem, setActiveModalItem] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [ticketSummary, setTicketSummary] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pws_ticket_summary')) || null; } catch { return null; }
  });
  const [klippekortSummary, setKlippekortSummary] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pws_kk_summary')) || null; } catch { return null; }
  });

  // Load user coupons (active only, max shown on home)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const u = session?.user;
      if (!u) {
        setUserCoupons([]);
        setCouponsLoading(false);
        localStorage.removeItem('pws_home_coupons_cache');
        return;
      }
      try {
        const ownUserId = (await supabase.from('users').select('id').eq('auth_user_id', u.id).maybeSingle()).data?.id;
        if (!ownUserId) {
          setUserCoupons([]);
          setCouponsLoading(false);
          return;
        }
        const { data: rows, error } = await supabase
          .from('user_coupons')
          .select('*, coupons_catalog(*)')
          .eq('deleted', false)
          .eq('used', false)
          .eq('user_id', ownUserId);
        if (error) throw error;
        const now = new Date();
        const active = (rows || [])
          .map(r => ({ id: r.id, ...r.coupons_catalog, createdAt: r.created_at }))
          .filter(c => !c.expiration || new Date(...c.expiration.split('-').map((v,i) => i===1 ? v-1 : v)) > now)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setUserCoupons(active);
        try { localStorage.setItem('pws_home_coupons_cache', JSON.stringify(active)); } catch {}
      } catch { setUserCoupons([]); }
      setCouponsLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  const formattedPoints = Number(userPoints || 0).toFixed(2).replace(/\.00$/, '');

  const formatNordicDate = (isoDate) => {
    if (!isoDate) return '-';
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) return '-';
    return `${day}.${month}.${year}`;
  };

  const openItemModal = (item) => {
    setActiveModalItem(item);
    setTimeout(() => setModalVisible(true), 20);
  };

  const closeItemModal = () => {
    setModalVisible(false);
    setTimeout(() => {
      setActiveModalItem(null);
      setDragY(0);
    }, 280);
  };

  // Body scroll lock while modal open
  useEffect(() => {
    if (!activeModalItem) return;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [activeModalItem]);

  // Global mouse drag listeners
  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e) => {
      const delta = e.clientY - startY;
      if (delta > 0) setDragY(delta);
    };
    const onMouseUp = () => {
      setIsDragging(false);
      if (dragY > 140) { closeItemModal(); } else { setDragY(0); }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startY, dragY]);

  const handleDragStart = (clientY) => { setIsDragging(true); setStartY(clientY); };
  const handleDragMove = (clientY) => {
    if (!isDragging) return;
    const delta = clientY - startY;
    if (delta > 0) setDragY(delta);
  };
  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 140) { closeItemModal(); } else { setDragY(0); }
  };

  const handleModalPrimaryAction = () => {
    if (!activeModalItem) return;
    if (activeModalItem.type === 'event') {
      navigate('/about');
    }
    closeItemModal();
  };

  const handleModalExploreAction = () => {
    navigate('/coupons');
    closeItemModal();
  };

  const isEventModal = activeModalItem?.type === 'event';
  const isCouponModal = Boolean(activeModalItem) && !isEventModal;
  const modalTagClass = 'modal-tag-yellow';
  const modalTagText = isEventModal
    ? ((activeModalItem?.badgeText || t('eventFallback')).trim() || t('eventFallback'))
    : t('coupon');
  const modalSubtitle = isEventModal
    ? formatNordicDate(activeModalItem?.eventDate)
    : (activeModalItem?.description || t('coupon'));
  const modalDateLabel = isEventModal ? t('dateLabel') : t('expiresLabel');
  const modalDateValue = isEventModal
    ? formatNordicDate(activeModalItem?.eventDate)
    : (activeModalItem?.expiration ? formatNordicDate(activeModalItem?.expiration) : t('noExpiryDate'));

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeItemModal();
      }
    };

    if (activeModalItem) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeModalItem]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user;
      if (currentUser) {
        setLikelyLoggedIn(true);
        localStorage.setItem('pws_auth', '1');
        try {
          const { data: userRow } = await supabase.from('users').select('id').eq('auth_user_id', currentUser.id).maybeSingle();
          const profile = await fetchOwnProfile(currentUser.id);
          if (profile) {
            setUserName(profile.name || t('defaultUserName'));
            setUserPoints(Number(profile.points) || 0);
            try { localStorage.setItem('pws_user_cache', JSON.stringify(profile)); } catch {}
          } else {
            setUserName(t('defaultUserName'));
            setUserPoints(0);
          }
          // Fetch tickets + klippekort summary
          try {
            if (userRow) {
              const [{ data: ticketRows }, { data: kkRows }] = await Promise.all([
                supabase.from('tickets').select('*').eq('user_id', userRow.id),
                supabase.from('klippekort').select('*').eq('user_id', userRow.id),
              ]);

              // A full-order refund (admin's "Full ordrerefundering") only ever sets
              // orders.fully_refunded - it never touches each ticket's own `refunded`
              // column (that's only set for a single-item refund, see backend's
              // /refund-payment). Checking row.refunded alone therefore keeps counting
              // every ticket from a fully-refunded order as still active. Tickets.jsx
              // already guards against this the same way - mirror it here.
              const orderIds = [...new Set([
                ...(ticketRows || []).map(t => t.order_id),
                ...(kkRows || []).map(k => k.order_id),
              ].filter(Boolean))];
              const { data: fullyRefundedOrderRows } = orderIds.length
                ? await supabase.from('orders').select('id').in('id', orderIds).eq('fully_refunded', true)
                : { data: [] };
              const fullyRefundedOrderIds = new Set((fullyRefundedOrderRows || []).map(o => o.id));

              let totalTickets = 0;
              let activeTickets = 0;
              let firstActiveLabel = '';
              (ticketRows || []).forEach(row => {
                if (!row.refunded && !fullyRefundedOrderIds.has(row.order_id)) {
                  const td = toTicketShape(row);
                  totalTickets++;
                  const isActive = isRoundBased(td) ? isRoundPlaying(td) : computeTicketRemainingSeconds(td) > 0;
                  if (isActive) {
                    activeTickets++;
                    if (!firstActiveLabel) firstActiveLabel = td.name || '';
                  }
                }
              });
              // Per-card stamps-left, kept in original insertion order so we can pick
              // the FIRST eligible card overall (same card KlippekortRedeem.jsx shows
              // first) instead of whichever has the most stamps left.
              const orderKK = (kkRows || [])
                .filter(kd => !fullyRefundedOrderIds.has(kd.order_id))
                .map(kd => {
                  const stampTotal = kd.stamp_amounts || 0;
                  const stampsLeft = stampTotal - (kd.used_stamps || 0);
                  return stampsLeft > 0 ? { id: kd.id, name: kd.name || 'Klippekort', stampsLeft, stampTotal } : null;
                })
                .filter(Boolean);
              const bestKK = orderKK[0] || null;
              const newTicketSummary = totalTickets > 0 ? { count: totalTickets, activeCount: activeTickets, label: firstActiveLabel } : null;
              const newKKSummary = bestKK || null;
              setTicketSummary(newTicketSummary);
              setKlippekortSummary(newKKSummary);
              try {
                if (newTicketSummary) localStorage.setItem('pws_ticket_summary', JSON.stringify(newTicketSummary));
                if (newKKSummary) localStorage.setItem('pws_kk_summary', JSON.stringify(newKKSummary));
              } catch {}
            }
          } catch (e) { /* ignore summary errors */ }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserName(t('defaultUserName'));
          setUserPoints(0);
        }
      } else {
        setLikelyLoggedIn(false);
        setUserName('');
        setUserPoints(0);
        setTicketSummary(null);
        setKlippekortSummary(null);
        localStorage.removeItem('pws_ticket_summary');
        localStorage.removeItem('pws_kk_summary');
        localStorage.removeItem('pws_user_cache');
        localStorage.removeItem('pws_home_coupons_cache');
        localStorage.removeItem('pws_auth');
      }
      setProfileLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [t]);


  const modal = activeModalItem && typeof document !== 'undefined'
    ? createPortal(
        <div
          className={`home-modal-overlay ${modalVisible ? 'visible' : 'hidden'}`}
          onClick={(e) => { if (e.target === e.currentTarget) closeItemModal(); }}
          style={{ opacity: modalVisible ? Math.max(0, 1 - dragY / 320) : 0 }}
        >
          <CloseOnBack onClose={closeItemModal} />
          <div
            className={`home-modal-sheet ${modalVisible ? 'visible' : 'hidden'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                transform: `translateY(${dragY}px)`,
                transition: isDragging ? 'none' : 'transform 0.28s ease'
              }}
              onTouchStart={(e) => { e.preventDefault(); handleDragStart(e.touches[0].clientY); }}
              onTouchMove={(e) => { e.preventDefault(); handleDragMove(e.touches[0].clientY); }}
              onTouchEnd={handleDragEnd}
              onMouseDown={(e) => handleDragStart(e.clientY)}
            >
              <div className="home-modal-handle" />
              <div className="home-modal-header">
                <div className="home-modal-header-left">
                  <div className="home-modal-icon">
                    {isEventModal ? <MdEventAvailable size={24} /> : <FiGift size={22} />}
                  </div>
                  <div>
                    <div className="home-modal-title">{activeModalItem.title}</div>
                    <div className="home-modal-subtitle">{modalSubtitle}</div>
                  </div>
                </div>
                <button className="home-modal-close" onClick={closeItemModal} aria-label={t('close')}>
                  <FiX size={16} />
                </button>
              </div>

              <div className="home-modal-body">
                <span className={`home-modal-tag ${modalTagClass}`}>{modalTagText}</span>

                <p className="home-modal-description">{activeModalItem.description}</p>

                <div className="home-modal-meta">
                  <div className="home-meta-row">
                    <FiCalendar />
                    <span>{modalDateLabel}</span>
                    <strong>{modalDateValue}</strong>
                  </div>
                  {isEventModal && activeModalItem.startTime && activeModalItem.endTime && (
                    <div className="home-meta-row">
                      <FiCalendar />
                      <span>{t('timeLabel')}</span>
                      <strong>{activeModalItem.startTime} til {activeModalItem.endTime}</strong>
                    </div>
                  )}
                  {isEventModal && activeModalItem.priceBadge && (
                    <div className="home-meta-row">
                      <FiCreditCard />
                      <span>{t('priceLabel')}</span>
                      <strong>{activeModalItem.priceBadge}</strong>
                    </div>
                  )}
                  {isCouponModal && activeModalItem.priceBadge && (
                    <div className="home-meta-row">
                      <FiCreditCard />
                      <span>{t('priceLabel')}</span>
                      <strong>{activeModalItem.priceBadge}</strong>
                    </div>
                  )}
                  {isCouponModal && activeModalItem.startTime && activeModalItem.endTime && (
                    <div className="home-meta-row">
                      <FiCalendar />
                      <span>{t('timeLabel')}</span>
                      <strong>{activeModalItem.startTime} til {activeModalItem.endTime}</strong>
                    </div>
                  )}
                  <div className="home-meta-row">
                    <FiTag />
                    <span>{t('statusLabel')}</span>
                    <strong>{isCouponModal ? t('active') : (activeModalItem.isActive === false ? t('inactive') : t('active'))}</strong>
                  </div>
                </div>

                {isEventModal && (
                  <button className="home-modal-btn" onClick={handleModalPrimaryAction}>
                    {t('seeMoreInfo')}
                  </button>
                )}
                <button className="home-modal-btn-subtle" onClick={handleModalExploreAction}>
                  {isEventModal ? t('seeMoreEvents') : t('seeMoreCoupons')}
                </button>
                <button className="home-modal-btn-ghost" onClick={closeItemModal}>{t('close')}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
    <div className="home-wrapper">
      {/* Welcome Card */}
      <div
        className={`welcome-card welcome-card-clickable ${likelyLoggedIn ? 'logged-in' : ''}${profileLoading ? ' welcome-card--loading' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => { if (!profileLoading) navigate(likelyLoggedIn ? '/profile' : '/login'); }}
        onKeyDown={(e) => { if (!profileLoading && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(likelyLoggedIn ? '/profile' : '/login'); } }}
      >
        {profileLoading ? (
          <>
            <div className="welcome-card-header-row">
              <h2 className="welcome-title welcome-skeleton-fill" style={{ width: '55%' }}>&nbsp;</h2>
            </div>
            <p className="welcome-desc welcome-skeleton-fill" style={{ width: '70%' }}>&nbsp;</p>
            <div className="points-chip-home welcome-skeleton-fill" style={{ width: '110px' }}>&nbsp;</div>
          </>
        ) : likelyLoggedIn ? (
          <>
            <div className="welcome-card-header-row">
              <h2 className="welcome-title">{t('welcomeBackTitle', { name: userName.toUpperCase() })}</h2>
              <FiChevronRight size={20} className="welcome-card-arrow" />
            </div>
            <p className="welcome-desc">{t('welcomeBackDesc')}</p>
            <div className="points-chip-home">
              <FiStar size={14} />
              {formattedPoints} {t('pointsSuffix')}
            </div>
          </>
        ) : (
          <>
            <div className="welcome-card-header-row">
              <h2 className="welcome-title">{t('welcomeGuestTitle')}</h2>
              <FiChevronRight size={20} className="welcome-card-arrow" />
            </div>
            <p className="welcome-desc">{t('welcomeGuestDesc')}</p>
            <div className="points-chip-home">
              <FiStar size={14} />
              {t('loginForPoints')}
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        className="buy-ticket-btn"
        onClick={() => { window.navigator.vibrate?.(10); navigate('/Products'); }}
      >
        <MdConfirmationNumber size={20} />
        {t('buyTicket')}
      </button>

      <div className="centered-divider"><span>{t('sectionMineTing')}</span></div>
      <div className={!profileLoading && !likelyLoggedIn ? 'quick-section mine-ting-group--locked' : 'quick-section'}>
        {!profileLoading && !likelyLoggedIn && (
          <div className="mine-ting-lock-overlay">
            <button type="button" className="mine-ting-lock-badge" onClick={() => navigate('/login')}>
              <FiLock size={18} />
              <span>{t('loginToSeeTicketsAndKlippekort')}</span>
            </button>
          </div>
        )}
        <div
          className="mine-ting-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/tickets')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/tickets'); } }}
        >
          <div className="mine-ting-icon" style={{ background: '#E07B20' }}>
            <MdConfirmationNumber size={24} />
          </div>
          <div className="mine-ting-text">
            <h4>{t('activeTickets')}</h4>
            {ticketSummary ? (
              <p>{ticketSummary.activeCount > 0 ? t(ticketSummary.activeCount > 1 ? 'activeCountPlural' : 'activeCount', { count: ticketSummary.activeCount }) : t('noActive')}{ticketSummary.label ? ` · ${ticketSummary.label}` : ''}</p>
            ) : (
              <p>{t('seeYourTickets')}</p>
            )}
          </div>
          <div className="mine-ting-right">
            {ticketSummary && <span className="mine-ting-badge">{t('totalCount', { count: ticketSummary.count })}</span>}
            <FiChevronRight size={16} className="mine-ting-arrow" />
          </div>
        </div>

        <div
          className="mine-ting-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/klippekort-redeem')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/klippekort-redeem'); } }}
        >
          <div className="mine-ting-icon" style={{ background: '#6B4FBB' }}>
            <MdConfirmationNumber size={24} />
          </div>
          <div className="mine-ting-text">
            <h4>{t('klippekort')}</h4>
            {klippekortSummary ? (
              <>
                <p>{t('punchesLeftOfTotal', { left: klippekortSummary.stampsLeft, total: klippekortSummary.stampTotal })}</p>
                <div className="mine-ting-dots">
                  {Array.from({ length: Math.min(klippekortSummary.stampTotal, 30) }).map((_, i) => (
                    <span
                      key={i}
                      className={`mine-ting-dot${i < klippekortSummary.stampsLeft ? ' filled' : ''}`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p>{t('seeYourKlippekort')}</p>
            )}
          </div>
          <div className="mine-ting-right">
            {klippekortSummary && <span className="mine-ting-badge" style={{ background: '#6B4FBB', color: '#fff' }}>{t('leftCount', { count: klippekortSummary.stampsLeft })}</span>}
            <FiChevronRight size={16} className="mine-ting-arrow" />
          </div>
        </div>
      </div>

      {(couponsLoading || userCoupons.length > 0) && (
        <>
          <div className="centered-divider"><span>{t('sectionKuponger')}</span></div>
          <div className="quick-section">
            {couponsLoading ? (
              [0, 1].map((i) => (
                <div key={i} className="tilbud-card tilbud-card--coupon tilbud-card--skeleton" aria-hidden="true">
                  <div className="tilbud-inner">
                    <div className="tilbud-badge tilbud-skeleton-shape" />
                    <div className="tilbud-text">
                      <div className="tilbud-skeleton-line tilbud-skeleton-line--tag" />
                      <div className="tilbud-skeleton-line tilbud-skeleton-line--title" />
                      <div className="tilbud-skeleton-line tilbud-skeleton-line--desc" />
                    </div>
                  </div>
                  <div className="coupon-strip">
                    <div className="tilbud-skeleton-line tilbud-skeleton-line--strip" />
                  </div>
                </div>
              ))
            ) : userCoupons.slice(0, 3).map((coupon) => {
              const isUnlimited = coupon.unlimited || coupon.expiration === null || !coupon.expiration;
              const expiringSoon = !isUnlimited && isExpiringSoon(coupon.expiration);
              const daysLeft = expiringSoon ? Math.max(0, Math.floor(getDaysUntilExpiry(coupon.expiration))) : null;
              return (
                <div
                  key={coupon.id}
                  className="tilbud-card tilbud-card--coupon"
                  role="button"
                  tabIndex={0}
                  onClick={() => openItemModal(coupon)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItemModal(coupon); } }}
                >
                  <div className="tilbud-inner">
                    <div className={`tilbud-badge ${expiringSoon ? 'tilbud-badge--orange' : 'tilbud-badge--green'}`}><FiGift size={22} /></div>
                    <div className="tilbud-text">
                      <div className="tilbud-tag-row">
                        <span className={`tilbud-tag ${expiringSoon ? 'tilbud-tag--orange' : 'tilbud-tag--green'}`}>
                          {expiringSoon ? t('expiringSoon') : t('coupon')}
                        </span>
                      </div>
                      <h3>{coupon.title}</h3>
                      <p>{coupon.description}</p>
                    </div>
                    <FiChevronRight size={16} className="tilbud-arrow" />
                  </div>
                  <div className="coupon-strip">
                    <div className="coupon-strip-left"><FiCreditCard size={13} /> {t('activeCoupon')}</div>
                    <span className="coupon-strip-code">
                      {isUnlimited
                        ? t('noExpiryDate')
                        : expiringSoon
                          ? t(daysLeft === 1 ? 'expiresInDays' : 'expiresInDaysPlural', { days: daysLeft })
                          : t('expiresOn', { date: formatNordicDate(coupon.expiration) })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Events section disabled for now - see the guarded useEffect above for the fetch logic. */}

      {/* Profile-style card buttons on Home page */}
      <div className="centered-divider"><span>{t('sectionPark')}</span></div>
      <ParkStatusCard />

      <div className="centered-divider"><span>{t('sectionHjelp')}</span></div>
      <div className="profile-menu-card">
        <ProfileCardButton
          icon={<FiHeadphones size={17} />}
          title={t('customerServiceTitle')}
          description={t('customerServiceDesc')}
          to="/support"
          color="teal"
          vibrate={20}
        />
        <ProfileCardButton
          icon={<FiSettings size={17} />}
          title={t('quickSettingsTitle')}
          description={t('quickSettingsDesc')}
          onClick={() => setShowLangSheet(true)}
          color="gray"
          vibrate={10}
        />
      </div>

      <div className="home-cards-wrapper" style={{ marginTop: 20 }}>
      </div>

    </div>
    {modal}
    {showLangSheet && (
      <BottomSheet
        icon={<FiSettings size={20} />}
        iconColor="blue"
        title={t('quickSettingsTitle')}
        subtitle={t('languageLabel')}
        onClose={() => setShowLangSheet(false)}
      >
        <LanguageSwitcher />
      </BottomSheet>
    )}
    </>
  );
}
